import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["childNodeIds"];
import { immerable } from 'immer';
import GridNode from './GridNode';
import { DEFAULT_BREAKPOINT_NAME, GRID_NODE_TYPES, STACK_DIRECTIONS } from './constants';
import { isGridContainerLayout, isManualPosition, isPegboardContainerLayout, isStackContainerLayout } from './typenameUtils';
import { iterateFrom, mergeIntoPath } from './utils';
import { ContainerTypename } from '../../__generated__/chirp/com/hubspot/content/grid/layouts/rpc/GridLayoutsRpc';
export default class ContainerNode extends GridNode {
  constructor(_ref) {
    let {
        childNodeIds = []
      } = _ref,
      containerNodeParams = _objectWithoutPropertiesLoose(_ref, _excluded);
    super(containerNodeParams);
    this[immerable] = true;
    this._childNodeIds = childNodeIds;
  }
  cloneAndChange(containerNodeParams) {
    const {
      id,
      childNodeIds,
      parentNodeId,
      value,
      treeRef
    } = containerNodeParams;
    const newNodeParams = {
      id: id !== null && id !== void 0 ? id : this._id,
      type: GRID_NODE_TYPES.CONTAINER,
      treeRef: treeRef !== null && treeRef !== void 0 ? treeRef : this._treeRef,
      childNodeIds: childNodeIds !== null && childNodeIds !== void 0 ? childNodeIds : this._childNodeIds,
      parentNodeId: parentNodeId !== null && parentNodeId !== void 0 ? parentNodeId : this._parentNodeId,
      value: value !== null && value !== void 0 ? value : this._value
    };
    return newNodeParams.treeRef.createNewContainer(newNodeParams);
  }
  serializeNode(options) {
    return Object.assign({}, this.getValue(), {
      children: this.getChildNodes().map(child => child.serializeNode(options)),
      __typename: ContainerTypename
    });
  }
  exportNode() {
    return {
      id: this.getId(),
      value: this.getValue(),
      parentNodeId: this.getParentNodeId(),
      childNodeIds: [...this.getChildNodeIds()]
    };
  }
  getChildNodes() {
    return this.getChildNodeIds().map(childNodeId => this.getTree().findNode(childNodeId));
  }
  mergeIntoValue(partialValue, path) {
    return this.cloneAndChange({
      value: mergeIntoPath(this._value, partialValue, path)
    });
  }
  mergeIntoStylesByPath(partialStyles, path) {
    return this.cloneAndChange({
      value: Object.assign({}, this._value, {
        styles: mergeIntoPath(this.getStyles(), partialStyles, path)
      })
    });
  }
  mergeIntoStyles(partialStyles) {
    return this.cloneAndChange({
      value: Object.assign({}, this._value, {
        styles: mergeIntoPath(this.getStyles(), partialStyles, ['breakpointStyles'])
      })
    });
  }
  isChildOfSection() {
    return this.getParentNode().isSection();
  }
  getLayout() {
    return this.getValue().layout;
  }
  getContainerLayout(breakpoint = 'default') {
    return this.getLayout().layout[breakpoint] || this.getLayout().layout.default;
  }
  isGrid(breakpoint = 'default') {
    return isGridContainerLayout(this.getContainerLayout(breakpoint));
  }
  isPegboard(breakpoint = 'default') {
    return isPegboardContainerLayout(this.getContainerLayout(breakpoint));
  }
  isStack(breakpoint = 'default') {
    return isStackContainerLayout(this.getContainerLayout(breakpoint));
  }
  isHorizontalStack(breakpoint = 'default') {
    const stackLayout = this.getContainerLayout(breakpoint);
    return isStackContainerLayout(stackLayout) && stackLayout.direction === STACK_DIRECTIONS.HORIZONTAL;
  }
  isVerticalStack(breakpoint = 'default') {
    const stackLayout = this.getContainerLayout(breakpoint);
    return isStackContainerLayout(stackLayout) && stackLayout.direction === STACK_DIRECTIONS.VERTICAL;
  }
  getBreakpointStyles(breakpoint = 'default') {
    const defaultStyles = Object.assign({}, this.getStyles().breakpointStyles.default);
    if (breakpoint !== 'default') {
      return Object.assign({}, defaultStyles, this.getStyles().breakpointStyles[breakpoint]);
    }
    return defaultStyles;
  }
  getStyles() {
    return this.getValue().styles;
  }
  getEffectiveContainerStyle(style, breakpoint, breakpointsByInheritance) {
    var _containerBreakpointS;
    const containerBreakpointStyles = this.getStyles().breakpointStyles;
    const styleAtBreakpoint = (_containerBreakpointS = containerBreakpointStyles[breakpoint]) === null || _containerBreakpointS === void 0 ? void 0 : _containerBreakpointS[style];
    if (styleAtBreakpoint !== undefined) {
      return {
        value: styleAtBreakpoint,
        inheritedBy: breakpoint
      };
    }
    const parentBreakpoint = breakpointsByInheritance[breakpoint];
    if (parentBreakpoint === undefined) {
      var _containerBreakpointS2;
      return {
        value: (_containerBreakpointS2 = containerBreakpointStyles[DEFAULT_BREAKPOINT_NAME]) === null || _containerBreakpointS2 === void 0 ? void 0 : _containerBreakpointS2[style],
        inheritedBy: DEFAULT_BREAKPOINT_NAME
      };
    }
    return this.getEffectiveContainerStyle(style, parentBreakpoint, breakpointsByInheritance);
  }
  getEffectiveContainerLayout(breakpoint, breakpointsByInheritance) {
    const containerLayout = this.getLayout().layout;
    const layoutAtBreakpoint = containerLayout[breakpoint];
    if (layoutAtBreakpoint) {
      return {
        value: layoutAtBreakpoint,
        inheritedBy: breakpoint
      };
    }
    const parentBreakpoint = breakpointsByInheritance[breakpoint];
    if (!parentBreakpoint) {
      return {
        value: containerLayout[DEFAULT_BREAKPOINT_NAME],
        inheritedBy: DEFAULT_BREAKPOINT_NAME
      };
    }
    return this.getEffectiveContainerLayout(parentBreakpoint, breakpointsByInheritance);
  }
  getLabel() {
    var _this$getValue$label;
    return (_this$getValue$label = this.getValue().label) !== null && _this$getValue$label !== void 0 ? _this$getValue$label : '';
  }
  getAllDescendantNodeIds() {
    const descendantNodeIds = [];
    iterateFrom(this, {
      includeStartNode: false,
      gridNodeCallback: ({
        gridNode
      }) => {
        descendantNodeIds.push(gridNode.getId());
      }
    });
    return descendantNodeIds;
  }
  findClosestOpenSpaceOnPegboard({
    targetNode,
    row,
    column,
    rowsSpanned,
    columnsSpanned,
    breakpoint = DEFAULT_BREAKPOINT_NAME
  }) {
    const containerLayout = this.getContainerLayout(breakpoint);
    if (!isPegboardContainerLayout(containerLayout)) {
      throw new Error(`Cannot find closest open space on pegboard: container is not a pegboard layout at breakpoint ${breakpoint}.`);
    }
    const maxRow = containerLayout.rows.length;
    const maxColumn = containerLayout.columns.length;
    const occupancyGrid = this.buildOccupancyGrid(this.getChildNodes(), targetNode.getId(), breakpoint, maxRow, maxColumn);

    // Extend the search space to include potential expansion area at the bottom
    // of the grid.
    const extendedMaxRow = maxRow + rowsSpanned;

    // Search for the closest available space in a spiral pattern starting from
    // the requested position.
    for (const {
      row: destRow,
      column: destCol
    } of this.generateSpiralSearchPositions(row, column, extendedMaxRow, maxColumn)) {
      // Abort if the position is outside the column bounds.
      if (destCol + columnsSpanned - 1 > maxColumn) {
        continue;
      }
      const isPositionExtendedPastOriginalGrid = destRow > maxRow;
      if (isPositionExtendedPastOriginalGrid) {
        // For positions extended past the original grid, check only the overlap
        // with the original grid
        const overlapRowsSpanned = maxRow - destRow + 1;
        if (!this.isIntersectingItemsOnGrid(occupancyGrid, destRow, destCol, overlapRowsSpanned, columnsSpanned, maxRow, maxColumn, false)) {
          const rowsNeededToFit = destRow + rowsSpanned - 1 - maxRow;
          return {
            row: destRow,
            column: destCol,
            spaceFound: false,
            rowsNeededToFit
          };
        }
      } else if (!this.isIntersectingItemsOnGrid(occupancyGrid, destRow, destCol, rowsSpanned, columnsSpanned, maxRow, maxColumn)) {
        // Check for positions within original grid dimensions
        return {
          row: destRow,
          column: destCol,
          spaceFound: true
        };
      }
    }

    // If no open space is found even in extended area, default to the requested position
    return {
      row,
      column,
      spaceFound: false,
      rowsNeededToFit: rowsSpanned
    };
  }
  getMinRowCount(breakpoint = DEFAULT_BREAKPOINT_NAME) {
    if (!this.isPegboard(breakpoint)) {
      throw new Error('getMinRowCount can only be called on pegboard containers');
    }
    let minRowCount = 1;
    for (const child of this.getChildNodes()) {
      var _gridSpanning$rowsSpa;
      if (!child.isModule()) continue;
      const styles = child.getEditorProvidedBreakpointStyles(breakpoint);
      const {
        gridPosition,
        gridSpanning
      } = styles;
      const rowStart = gridPosition && isManualPosition(gridPosition) ? gridPosition.rowStart : 1;
      const rowsSpanned = (_gridSpanning$rowsSpa = gridSpanning === null || gridSpanning === void 0 ? void 0 : gridSpanning.rowsSpanned) !== null && _gridSpanning$rowsSpa !== void 0 ? _gridSpanning$rowsSpa : 1;
      minRowCount = Math.max(minRowCount, rowStart + rowsSpanned - 1);
    }
    return minRowCount;
  }

  /**
   * Creates a 2D boolean grid that represents the occupancy of the grid.
   * @param childNodes - The child nodes of the container.
   * @param targetNodeId - The ID of the target node.
   * @param breakpoint - The breakpoint of the grid.
   * @param maxRows - The maximum number of rows in the grid.
   * @param maxColumns - The maximum number of columns in the grid.
   * @returns The occupancy grid.
   */
  buildOccupancyGrid(childNodes, targetNodeId, breakpoint, maxRows, maxColumns) {
    const grid = Array.from({
      length: maxRows + 1
    }, () => Array(maxColumns + 1).fill(false));
    for (const node of childNodes) {
      // Skip the target node, we don't want to count it as occupied.
      if (node.getId() === targetNodeId) {
        continue;
      }
      let gridPosition;
      let gridSpanning;
      if (node.isModule()) {
        const styles = node.getEditorProvidedBreakpointStyles(breakpoint);
        gridPosition = styles.gridPosition;
        gridSpanning = styles.gridSpanning;
      } else {
        const styles = node.getBreakpointStyles(breakpoint);
        gridPosition = styles.gridPosition;
        gridSpanning = styles.gridSpanning;
      }
      if (gridSpanning && gridPosition && isManualPosition(gridPosition)) {
        const row = gridPosition.rowStart;
        const column = gridPosition.columnStart;
        const rowsSpanned = gridSpanning.rowsSpanned || 1;
        const columnsSpanned = gridSpanning.columnsSpanned || 1;

        // Mark the cells in the grid that the node occupies.
        for (let r = row; r < row + rowsSpanned; r++) {
          for (let c = column; c < column + columnsSpanned; c++) {
            if (r >= 1 && r <= maxRows && c >= 1 && c <= maxColumns) {
              grid[r][c] = true;
            }
          }
        }
      }
    }
    return grid;
  }

  /**
   * Generates search positions in expanding layers around the starting position.
   * Uses Manhattan distance to generate positions layer by layer.
   * @param startRow - The row of the start position.
   * @param startCol - The column of the start position.
   * @param maxRow - The maximum number of rows in the grid.
   * @param maxCol - The maximum number of columns in the grid.
   * @returns A generator of the search positions.
   */
  *generateSpiralSearchPositions(startRow, startCol, maxRow, maxCol) {
    // Starting from 0 (which represents the starting position), the maximum
    // distance is the furthest distance from the starting position to the edge
    // of the grid.
    const maxDistance = Math.max(maxRow - 1, maxCol - 1, startRow - 1, startCol - 1) + Math.max(maxRow, maxCol);

    // Iterate through each distance level
    for (let distance = 0; distance <= maxDistance; distance++) {
      // Starting from the top left corner, iterate through each row in the
      // current distance level.
      for (let deltaRow = -distance; deltaRow <= distance; deltaRow++) {
        // Calculate the number of columns to iterate for the current distance
        // level.
        const columnsToIterate = distance - Math.abs(deltaRow);
        // Generate the column deltas for the current distance level.
        const columnDeltas = columnsToIterate === 0 ? [0] : [-columnsToIterate, columnsToIterate];

        // Iterate through each column in the current distance level.
        for (const deltaCol of columnDeltas) {
          const row = startRow + deltaRow;
          const col = startCol + deltaCol;

          // If the position is within the search bounds.
          if (row >= 1 && row <= maxRow && col >= 1 && col <= maxCol) {
            yield {
              row,
              column: col
            };
          }
        }
      }
    }
  }

  /**
   * Checks if the target area intersects with any occupied cells in the grid.
   * @param grid - The occupancy grid.
   * @param row - The row of the target area.
   * @param column - The column of the target area.
   * @param rowsSpanned - The number of rows spanned by the target area.
   * @param columnsSpanned - The number of columns spanned by the target area.
   * @param maxRow - The maximum number of rows in the grid.
   * @param maxCol - The maximum number of columns in the grid.
   * @param checkGridBounds - Whether to check if the target area is within the grid bounds.
   * @returns True if the target area intersects with any occupied cells in the grid, false otherwise.
   */
  isIntersectingItemsOnGrid(grid, row, column, rowsSpanned, columnsSpanned, maxRow, maxCol, checkGridBounds = true) {
    // If the target area is outside the grid, we consider it to be intersecting
    if (checkGridBounds && (row < 1 || column < 1 || row + rowsSpanned - 1 > maxRow || column + columnsSpanned - 1 > maxCol)) {
      return true;
    }

    // Check if any of the cells in the target area are occupied
    for (let r = row; r < row + rowsSpanned; r++) {
      for (let c = column; c < column + columnsSpanned; c++) {
        if (grid[r][c]) {
          return true;
        }
      }
    }
    return false;
  }
}